import React from 'react';
import { Link } from 'react-router-dom';
import HeroSlider from '../components/HeroSlider';
import StatsBar from '../components/StatsBar';
import Reveal from '../components/Reveal';
import { useSlider } from '../hooks/useAnimations';
import { programmes, features, testimonials } from '../data/siteData';
import IMG from '../data/images';

const Home = () => {
  const { index: tIdx, goTo: tGo } = useSlider(testimonials.length, 5000);

  return (
    <div>
      <HeroSlider />

      {/* ── Nurturing Banner ── */}
      <section style={{ padding: '80px 24px', background: 'var(--cream)', position: 'relative', overflow: 'hidden' }}>
        <div className="container" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48, alignItems: 'center' }} id="home-about">
          <Reveal direction="left">
            <img src={IMG.kidsPlaying} alt="Children playing" style={{ borderRadius: 20, width: '100%', height: 360, objectFit: 'cover', boxShadow: 'var(--shadow-l)' }} />
          </Reveal>
          <Reveal direction="right" delay={0.15}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold)', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: 10 }}>About Us</div>
            <h2 style={{ fontFamily: 'var(--fh)', fontSize: '2.2rem', color: 'var(--navy)', lineHeight: 1.2, marginBottom: 16 }}>Nurturing Curiosity, Inspiring Confidence</h2>
            <p style={{ color: 'var(--body)', lineHeight: 1.9, marginBottom: 20, fontSize: '0.95rem' }}>
              At Finland Early Years, we believe childhood is not a race — it's a journey. Rooted in Finland's world-renowned education philosophy, we create spaces where children discover, imagine, and grow at their own beautiful pace.
            </p>
            <p style={{ color: 'var(--body)', lineHeight: 1.9, marginBottom: 28, fontSize: '0.95rem' }}>
              Every day is an adventure — from outdoor exploration to creative play, from music circles to science experiments. No worksheets. No pressure. Just joy.
            </p>
            <Link to="/about" className="btn btn-outline-dark">Learn More About Us</Link>
          </Reveal>
        </div>
        <style>{`@media(max-width:768px){#home-about{grid-template-columns:1fr!important;}}`}</style>
      </section>

      {/* ── Programmes ── */}
      <section className="sec" style={{ padding: '100px 24px' }}>
        <div className="container">
          <Reveal>
            <div className="gold-bar" />
            <h2 className="sec-heading">Our Programmes</h2>
            <p className="sec-sub">Age-appropriate Finnish-inspired learning journeys for every stage of early childhood.</p>
          </Reveal>
          <div style={{ display: 'flex', gap: 20, overflowX: 'auto', paddingBottom: 16, scrollSnapType: 'x mandatory' }}>
            {programmes.map((p, i) => (
              <div key={p.id} className="anim-up" style={{ width: 260, minWidth: 260, flex: '0 0 auto', scrollSnapAlign: 'start', animationDelay: `${i * 0.08}s` }}>
                <div className="card" style={{ height: '100%', borderTop: `4px solid ${p.color}` }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: 10 }}>{p.icon}</div>
                  <h3 style={{ fontFamily: 'var(--fh)', fontWeight: 700, fontSize: '1.1rem', color: 'var(--navy)', marginBottom: 4 }}>{p.name}</h3>
                  <div style={{ color: 'var(--gold)', fontWeight: 600, fontSize: '0.78rem', marginBottom: 14 }}>{p.age}</div>
                  <p style={{ color: 'var(--body)', lineHeight: 1.7, fontSize: '0.88rem' }}>{p.short}</p>
                </div>
              </div>
            ))}
          </div>
          <Reveal style={{ textAlign: 'center', marginTop: 36 }}>
            <Link to="/programmes" className="btn btn-outline-dark">View All Programmes →</Link>
          </Reveal>
        </div>
      </section>

      <StatsBar />

      {/* ── Admission CTA with image ── */}
      <section style={{ position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0 }}>
          <img src={IMG.classroom} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(27,45,91,0.92), rgba(15,29,61,0.95))' }} />
        </div>
        <div className="container" style={{ position: 'relative', zIndex: 2, padding: '80px 24px', display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 40, alignItems: 'center' }} id="cta-grid">
          <Reveal direction="left">
            <div style={{ color: 'var(--gold)', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', marginBottom: 10 }}>Admissions Open 2026–27</div>
            <h2 style={{ fontFamily: 'var(--fh)', fontSize: '2.4rem', color: '#fff', lineHeight: 1.2, marginBottom: 16 }}>Begin Safe and Smiling</h2>
            <p style={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.8, marginBottom: 28, fontSize: '0.95rem' }}>
              A safe & joyful start to education. Book a personalised campus tour and see the Finland difference firsthand.
            </p>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <Link to="/admissions" className="btn btn-gold">Enquire Now</Link>
              <Link to="/contact" className="btn btn-outline">Find a School</Link>
            </div>
          </Reveal>
          <Reveal direction="right" delay={0.15}>
            <img src={IMG.kidsArt} alt="" style={{ borderRadius: 16, width: '100%', height: 300, objectFit: 'cover', boxShadow: '0 8px 30px rgba(0,0,0,0.3)' }} />
          </Reveal>
        </div>
        <style>{`@media(max-width:768px){#cta-grid{grid-template-columns:1fr!important;}}`}</style>
      </section>

      {/* ── Features ── */}
      <section className="sec" style={{ padding: '100px 24px' }}>
        <div className="container">
          <Reveal>
            <div className="gold-bar" />
            <h2 className="sec-heading">The Finland Advantage</h2>
            <p className="sec-sub">What makes Finnish education the best in the world — and how we bring every element into our classrooms.</p>
          </Reveal>
          <div className="g3">
            {features.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="card-cream" style={{ height: '100%', textAlign: 'center' }}>
                  <div style={{ fontSize: '2.4rem', marginBottom: 12 }}>{f.icon}</div>
                  <h3 style={{ fontFamily: 'var(--fh)', fontSize: '1.08rem', color: 'var(--navy)', marginBottom: 8 }}>{f.title}</h3>
                  <p style={{ color: 'var(--body)', lineHeight: 1.7, fontSize: '0.88rem' }}>{f.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="sec-navy" style={{ padding: '90px 24px' }}>
        <div className="container-sm" style={{ textAlign: 'center' }}>
          <Reveal><div className="gold-bar" /><h2 className="sec-heading-w">What Parents Say</h2></Reveal>
          <div style={{ minHeight: 260, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginTop: 40 }}>
            <div key={tIdx} style={{ animation: 'fadeUp 0.5s ease-out' }}>
              <img src={[IMG.parent1, IMG.parent2, IMG.parent3, IMG.parent4][tIdx]} alt="" style={{ width: 70, height: 70, borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--gold)', margin: '0 auto 18px' }} />
              <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '1.12rem', lineHeight: 1.9, fontStyle: 'italic', maxWidth: 540, margin: '0 auto 24px' }}>
                "{testimonials[tIdx].text}"
              </p>
              <div style={{ color: 'var(--gold)', fontWeight: 700 }}>{testimonials[tIdx].name}</div>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem', marginTop: 4 }}>{testimonials[tIdx].role}</div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 32 }}>
              {testimonials.map((_, i) => (
                <button key={i} onClick={() => tGo(i)} style={{
                  width: i === tIdx ? 34 : 10, height: 10, borderRadius: 5,
                  background: i === tIdx ? 'var(--gold)' : 'rgba(255,255,255,0.2)',
                  border: 'none', cursor: 'pointer', transition: 'all 0.35s',
                }} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Gallery Preview ── */}
      <section className="sec-alt" style={{ padding: '80px 24px' }}>
        <div className="container">
          <Reveal>
            <div className="gold-bar" /><h2 className="sec-heading">Life at Finland Early Years</h2>
            <p className="sec-sub">A glimpse into the everyday magic of our classrooms and playgrounds.</p>
          </Reveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }} id="gallery-preview-grid">
            {IMG.gallery.slice(0, 6).map((img, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div style={{
                  borderRadius: 12, overflow: 'hidden', position: 'relative',
                  height: i === 0 || i === 5 ? 280 : 200, transition: 'transform 0.4s',
                  cursor: 'pointer',
                }}>
                  <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s' }}
                    onMouseEnter={e => e.target.style.transform = 'scale(1.06)'}
                    onMouseLeave={e => e.target.style.transform = 'scale(1)'}
                  />
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal style={{ textAlign: 'center', marginTop: 36 }}>
            <Link to="/gallery" className="btn btn-outline-dark">View Full Gallery →</Link>
          </Reveal>
        </div>
        <style>{`
          @media (max-width: 768px) { #gallery-preview-grid { grid-template-columns: repeat(2,1fr) !important; } }
          @media (max-width: 480px) { #gallery-preview-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </section>

      {/* ── Final CTA ── */}
      <section style={{ padding: '80px 24px', background: 'var(--gold)', textAlign: 'center' }}>
        <Reveal>
          <h2 style={{ fontFamily: 'var(--fh)', fontSize: '2.2rem', color: 'var(--navy)', marginBottom: 14 }}>
            Begin Your Child's Finnish Journey
          </h2>
          <p style={{ color: 'var(--navy)', opacity: 0.7, maxWidth: 480, margin: '0 auto 30px', lineHeight: 1.8 }}>
            Admissions are open for 2026–27. Schedule a campus visit today.
          </p>
          <Link to="/admissions" className="btn btn-navy">Schedule a Visit</Link>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
